import { useEffect, useRef, useState } from "react";
import type { EbayPublicationDraft, EbayPublicationDraftSave, EbayPublicationSelection } from "../domain/ebayPublication";

export interface EbaySetupRecovery { baseVersion: string | null; selection: EbayPublicationSelection; request?: EbayPublicationDraftSave }
const encoded = (value: unknown): string => JSON.stringify(value, (_key, part) => part && typeof part === "object" && !Array.isArray(part) ? Object.fromEntries(Object.entries(part).sort(([a], [b]) => a.localeCompare(b))) : part);
export const sameEbaySetup = (a: unknown, b: unknown) => encoded(a) === encoded(b);
export function readEbaySetupRecovery(key: string): EbaySetupRecovery | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw || raw.length > 100_000) return;
    const saved = JSON.parse(raw) as EbaySetupRecovery;
    if (saved && (saved.baseVersion === null || typeof saved.baseVersion === "string") && typeof saved.selection?.categoryID === "string" && saved.selection.specifics && typeof saved.selection.specifics === "object") return saved;
  } catch { /* Recovery is optional; cloud save status remains authoritative. */ }
}

/** One save at a time, with exact retries after an uncertain acknowledgement. */
export function useEbayPublicationDraft(input: {
  storageKey: string; values: EbayPublicationSelection; edited: boolean; active: boolean; recoveryPending?: boolean;
  save: (request: EbayPublicationDraftSave) => Promise<EbayPublicationDraft>;
}) {
  const current = useRef(input); current.current = input;
  const acknowledged = useRef<EbayPublicationDraft>();
  const baseline = useRef<EbayPublicationSelection>();
  const initialized = useRef(false), mounted = useRef(true);
  const pending = useRef<EbayPublicationDraftSave>();
  const inFlight = useRef<Promise<EbayPublicationDraft>>();
  const blocked = useRef(false);
  const [status, setStatus] = useState<"idle" | "changed" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<unknown>();
  const [, render] = useState(0);
  const dirty = initialized.current && input.edited && !sameEbaySetup(input.values, baseline.current);
  const dirtyRef = useRef(dirty); dirtyRef.current = dirty;
  function recover(selection: EbayPublicationSelection, request = pending.current) {
    try { localStorage.setItem(current.current.storageKey, JSON.stringify({ baseVersion: acknowledged.current?.version ?? null, selection, request } satisfies EbaySetupRecovery)); } catch { /* Never claim a local copy was saved. */ }
  }
  function clearRecovery(selection: EbayPublicationSelection) {
    try {
      // Another tab may have written its own unsaved copy to this key.
      if (sameEbaySetup(readEbaySetupRecovery(current.current.storageKey)?.selection, selection)) localStorage.removeItem(current.current.storageKey);
    } catch { /* Optional local recovery. */ }
  }
  function reset(draft: EbayPublicationDraft | undefined, values: EbayPublicationSelection, retryRequest?: EbayPublicationDraftSave) {
    acknowledged.current = draft; baseline.current = values; initialized.current = true;
    pending.current = retryRequest; blocked.current = false;
    setError(undefined); setStatus(draft ? "saved" : "idle"); render((value) => value + 1);
  }
  async function flush(): Promise<EbayPublicationDraft> {
    // Wait for the current acknowledgement, then save any answers typed during it.
    while (inFlight.current) await inFlight.current;
    if (!initialized.current) throw new Error("Wait for your saved setup to load before saving.");
    if (current.current.recoveryPending) throw new Error("Compare your unsaved answers before saving this setup.");
    const values = structuredClone(current.current.values);
    if (acknowledged.current && !pending.current && sameEbaySetup(values, baseline.current)) return acknowledged.current;
    const send = pending.current ?? { expectedVersion: acknowledged.current?.version ?? null, mutationID: crypto.randomUUID(), selection: values };
    pending.current = send; recover(values, send); blocked.current = false;
    if (mounted.current) { setStatus("saving"); setError(undefined); }
    const task = current.current.save(send).then((draft) => {
      acknowledged.current = draft; baseline.current = send.selection; pending.current = undefined;
      clearRecovery(send.selection);
      if (mounted.current) { setStatus(sameEbaySetup(current.current.values, send.selection) ? "saved" : "changed"); render((value) => value + 1); }
      return draft;
    }).catch((reason: unknown) => {
      blocked.current = true;
      if (mounted.current) { setError(reason); setStatus("error"); }
      throw reason;
    }).finally(() => { inFlight.current = undefined; });
    inFlight.current = task;
    const draft = await task;
    if (!sameEbaySetup(current.current.values, draft.selection)) return flush();
    return draft;
  }
  const valuesKey = encoded(input.values);
  useEffect(() => {
    if (!dirty || input.recoveryPending) return;
    recover(input.values);
    if (!input.active || inFlight.current || blocked.current) return;
    setStatus("changed");
    const timer = window.setTimeout(() => { void flush().catch(() => undefined); }, 650);
    return () => window.clearTimeout(timer);
  }, [valuesKey, dirty, input.active, input.recoveryPending, status]);
  useEffect(() => {
    mounted.current = true;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current && !inFlight.current) return;
      event.preventDefault(); event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => { mounted.current = false; window.removeEventListener("beforeunload", beforeUnload); };
  }, []);
  return { dirty, status, error, flush, reset, clearRecovery, draft: acknowledged.current };
}
